import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { 
  CheckCircle, 
  Clock, 
  XCircle, 
  User, 
  Users, 
  FileText, 
  ArrowRight,
  AlertCircle,
  Play,
  Plus,
  X
} from "lucide-react";
import { cn } from "@/lib/utils";
import { UserSelector } from "./UserSelector";

interface WorkflowStep {
  id: string;
  title: string;
  description: string;
  status: 'completed' | 'active' | 'pending' | 'rejected';
  users?: { id: string; name: string; designation?: string }[];
  icon: React.ReactNode;
  isCurrentStep?: boolean;
}

interface WorkflowSidebarProps {
  currentStatus: 'draft' | 'under_review' | 'pending_approval' | 'approved' | 'rejected';
  reviewers: { id: string; name: string; designation?: string }[];
  approvers: { id: string; name: string; designation?: string }[];
  creator: { name: string; designation?: string } | null;
  onReviewersChange?: (reviewers: string[]) => void;
  onApproversChange?: (approvers: string[]) => void;
  companyId?: string;
  showWorkflow?: boolean;
}

export function WorkflowSidebar({
  currentStatus,
  reviewers,
  approvers,
  creator,
  onReviewersChange,
  onApproversChange,
  companyId,
  showWorkflow = true,
}: WorkflowSidebarProps) {
  
  const getStepStatus = (stepKey: string): 'completed' | 'active' | 'pending' | 'rejected' => {
    if (currentStatus === 'rejected') {
      return stepKey === 'draft' ? 'completed' : 'rejected';
    }
    
    const statusOrder = ['draft', 'under_review', 'pending_approval', 'approved'];
    const currentIndex = statusOrder.indexOf(currentStatus);
    const stepIndex = statusOrder.indexOf(stepKey);
    
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'active';
    return 'pending';
  };

  const steps: WorkflowStep[] = [
    {
      id: 'draft',
      title: 'Draft',
      description: 'Policy is being created and edited',
      status: getStepStatus('draft'),
      users: creator ? [{ id: 'creator', name: creator.name, designation: creator.designation }] : [],
      icon: <FileText className="w-4 h-4" />,
      isCurrentStep: currentStatus === 'draft'
    },
    {
      id: 'under_review',
      title: 'Under Review',
      description: 'Policy is being reviewed by assigned reviewers',
      status: getStepStatus('under_review'),
      users: reviewers,
      icon: <Users className="w-4 h-4" />,
      isCurrentStep: currentStatus === 'under_review'
    },
    {
      id: 'pending_approval',
      title: 'Pending Approval',
      description: 'Policy awaiting final approval',
      status: getStepStatus('pending_approval'),
      users: approvers,
      icon: <Clock className="w-4 h-4" />,
      isCurrentStep: currentStatus === 'pending_approval'
    },
    {
      id: 'approved',
      title: 'Approved',
      description: 'Policy has been approved and is active',
      status: getStepStatus('approved'),
      users: approvers,
      icon: <CheckCircle className="w-4 h-4" />,
      isCurrentStep: currentStatus === 'approved'
    }
  ];

  const getStepStyles = (status: string, isCurrentStep: boolean = false) => {
    switch (status) {
      case 'completed':
        return {
          container: 'bg-green-50 border-green-200',
          icon: 'bg-green-500 text-white',
          title: 'text-green-900',
          description: 'text-green-700',
          connector: 'bg-green-500',
          badge: 'bg-green-100 text-green-800'
        };
      case 'active':
        return {
          container: 'bg-blue-50 border-blue-200 ring-2 ring-blue-300',
          icon: 'bg-blue-500 text-white',
          title: 'text-blue-900 font-semibold',
          description: 'text-blue-700',
          connector: 'bg-blue-500',
          badge: 'bg-blue-100 text-blue-800'
        };
      case 'rejected':
        return {
          container: 'bg-red-50 border-red-200',
          icon: 'bg-red-500 text-white',
          title: 'text-red-900',
          description: 'text-red-700',
          connector: 'bg-red-500',
          badge: 'bg-red-100 text-red-800'
        };
      default:
        return {
          container: 'bg-gray-50 border-gray-200',
          icon: 'bg-gray-300 text-gray-600',
          title: 'text-gray-700',
          description: 'text-gray-500',
          connector: 'bg-gray-300',
          badge: 'bg-gray-100 text-gray-600'
        };
    }
  };

  const getStatusBadge = (status: string, isCurrentStep: boolean) => {
    if (isCurrentStep) {
      return <Badge className="bg-blue-100 text-blue-800">Current Step</Badge>;
    }
    
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
      case 'active':
        return <Badge className="bg-blue-100 text-blue-800">Active</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      default:
        return <Badge variant="secondary">Pending</Badge>;
    }
  };

  return (
    <div className="space-y-4">
      {/* Assignment Section */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="w-5 h-5 text-blue-600" />
            Assign Workflow
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Reviewers Assignment */}
          <div className="space-y-2">
            <Label htmlFor="reviewers">Assign Reviewers *</Label>
            <UserSelector
              label="Reviewers"
              selectedUsers={reviewers.map(r => r.id)}
              onUsersChange={(userIds) => onReviewersChange?.(userIds)}
              allowedRoles={['Reviewer', 'Admin']}
            />
            {reviewers.length > 0 && (
              <div className="space-y-1">
                {reviewers.map((reviewer) => (
                  <div key={reviewer.id} className="flex items-center justify-between bg-blue-50 px-2 py-1 rounded text-xs">
                    <span className="font-medium">{reviewer.name}</span>
                    <span className="text-muted-foreground">{reviewer.designation}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => onReviewersChange?.(reviewers.filter(r => r.id !== reviewer.id).map(r => r.id))}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Approvers Assignment */}
          <div className="space-y-2">
            <Label htmlFor="approvers">Assign Approvers</Label>
            <UserSelector
              label="Approvers"
              selectedUsers={approvers.map(a => a.id)}
              onUsersChange={(userIds) => onApproversChange?.(userIds)}
              allowedRoles={['Approver', 'Admin']}
            />
            {approvers.length > 0 && (
              <div className="space-y-1">
                {approvers.map((approver) => (
                  <div key={approver.id} className="flex items-center justify-between bg-green-50 px-2 py-1 rounded text-xs">
                    <span className="font-medium">{approver.name}</span>
                    <span className="text-muted-foreground">{approver.designation}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => onApproversChange?.(approvers.filter(a => a.id !== approver.id).map(a => a.id))}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Workflow Visualization */}
      {showWorkflow && <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Play className="w-5 h-5 text-blue-600" />
            Approval Workflow
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {steps.map((step, index) => {
            const styles = getStepStyles(step.status, step.isCurrentStep);
            const isLast = index === steps.length - 1;
            
            return (
              <div key={step.id} className="relative">
                {/* Step Container */}
                <div className={cn(
                  "relative p-4 rounded-lg border transition-all duration-200",
                  styles.container
                )}>
                  {/* Step Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {/* Icon */}
                      <div className={cn(
                        "flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0",
                        styles.icon
                      )}>
                        {step.status === 'rejected' && step.id !== 'draft' ? (
                          <XCircle className="w-4 h-4" />
                        ) : (
                          step.icon
                        )}
                      </div>
                      
                      {/* Title */}
                      <h4 className={cn("text-sm font-medium", styles.title)}>
                        {step.title}
                      </h4>
                    </div>
                    
                    {/* Status Badge */}
                    {getStatusBadge(step.status, step.isCurrentStep)}
                  </div>
                  
                  {/* Description */}
                  <p className={cn("text-xs mb-3", styles.description)}>
                    {step.description}
                  </p>
                  
                  {/* Users */}
                  {step.users && step.users.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-muted-foreground">
                        {step.id === 'draft' ? 'Creator' : 
                         step.id === 'under_review' ? 'Reviewers' : 
                         'Approvers'}:
                      </div>
                      <div className="space-y-1">
                        {step.users.map((user, userIndex) => (
                          <div key={userIndex} className="flex items-center gap-2">
                            <User className="w-3 h-3 text-current opacity-60" />
                            <div className="text-xs">
                              <span className="font-medium">{user.name}</span>
                              {user.designation && (
                                <span className="text-current opacity-60 ml-1">
                                  • {user.designation}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Empty State for Users */}
                  {(!step.users || step.users.length === 0) && (
                    <div className="text-xs text-muted-foreground italic">
                      {step.id === 'draft' ? 'Policy creator' : 
                       step.id === 'under_review' ? 'No reviewers assigned' : 
                       'No approvers assigned'}
                    </div>
                  )}
                </div>
                
                {/* Connector Arrow */}
                {!isLast && (
                  <div className="flex justify-center py-2">
                    <ArrowRight className={cn(
                      "w-4 h-4",
                      step.status === 'completed' ? 'text-green-500' : 'text-gray-400'
                    )} />
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>}

      {showWorkflow && <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            Workflow Tips
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground space-y-2">
          <p>• Assign reviewers and approvers before submitting</p>
          <p>• Reviewers can provide feedback and suggestions</p>
          <p>• Approvers make the final decision on policy approval</p>
          <p>• You can edit the policy while it's in draft status</p>
        </CardContent>
      </Card>}
    </div>
  );
}
